/**
 * ProductEditor — DIE eine Bearbeitungsfläche für Produkte (ersetzt
 * ProductEditModal, ProductCardSheet und ProductEditPanel).
 *
 * Ein Sheet, ein Speichern: Stammdaten · Preise · Varianten (Preisebenen,
 * Gewicht, Grundpreis) · EWK & Gesetzliches · Lager · Lieferanten ·
 * Alt-Varianten (Archiv, lesend) · Kanäle. Props sind drop-in-kompatibel
 * zum alten Modal: { product, open, onOpenChange } — product=null → Neuanlage.
 *
 * Regeln: Shopify = Content-Master (Titel/SKU/Preise/Gewicht/Grundpreis
 * werden gespiegelt), FieldConnect = Operations-Master (B2B, Kundenpreise,
 * Bestand, EWK bleiben hier). Siehe useProductEditor.js.
 */
import { ImageOff, Loader2, Recycle, Save, X } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { thumb, IMG_PROPS } from '@/lib/imageUrl';
import { useOptionalAuth } from '@/api/AuthContext';
import { useProductEditor } from './useProductEditor';
import { Section, Field, NumInput } from './editor/EditorPrimitives';
import { StammdatenSection, PreiseSection, LagerSection } from './editor/ProductEditorStammdaten';
import ProductEditorVarianten from './editor/ProductEditorVarianten';
import EwkKategorienPicker from './editor/EwkKategorienPicker';
import EwkAbgabeVorschau from './editor/EwkAbgabeVorschau';
import ProductEditorLieferanten from './editor/ProductEditorLieferanten';
import ProductEditorArchiv from './editor/ProductEditorArchiv';
import ProductEditorKanaele from './editor/ProductEditorKanaele';

function StatusBadge({ product, isActive }) {
  if (!isActive) return <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-slate-200 text-slate-600">Inaktiv</span>;
  if (product?.channel_shopify) return <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200">Shopify</span>;
  return <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-slate-100 text-slate-500">nur FieldConnect</span>;
}

export default function ProductEditor({ product, open, onOpenChange }) {
  const ed = useProductEditor({ product, open, onOpenChange });
  const auth = useOptionalAuth();
  const isAdmin = !!auth?.isAdmin;
  const { form, isNew } = ed;
  const p = form.product;
  const set = ed.setProductField;
  const heroImage = ed.images[0]?.url || p.image_url || product?.image_url || product?.bild_url;
  const activeRows = form.rows.filter((r) => !r._removed);

  return (
    <Sheet open={open} onOpenChange={(v) => (v ? onOpenChange(true) : ed.requestClose())}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-3xl p-0 gap-0 flex flex-col [padding-top:0px] [padding-right:0px] [padding-bottom:0px]"
        onInteractOutside={(e) => { if (ed.dirty || ed.saving) e.preventDefault(); }}
      >
        {/* Kopf */}
        <header className="shrink-0 border-b border-slate-200 bg-white px-4 sm:px-6 py-4 pr-14 [padding-top:max(1rem,env(safe-area-inset-top))]">
          <div className="flex items-start gap-3">
            {heroImage ? (
              <img {...IMG_PROPS} width={64} height={64} src={thumb(heroImage, 128)} alt=""
                className="w-16 h-16 rounded-xl object-contain bg-white border border-slate-200 shrink-0" />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                <ImageOff className="w-5 h-5 text-slate-400" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <SheetTitle className="font-display text-xl font-bold tracking-tight text-brand-950 truncate">
                {isNew ? 'Neues Produkt' : (p.name || product?.name || 'Produkt')}
              </SheetTitle>
              <SheetDescription className="flex items-center gap-2 flex-wrap mt-1">
                <span className="font-mono text-xs text-slate-500">{p.sku || product?.sku || 'ohne SKU'}</span>
                <StatusBadge product={product} isActive={p.is_active} />
                {ed.dirty && <span className="text-[10px] uppercase tracking-wide text-amber-700">ungespeichert</span>}
              </SheetDescription>
            </div>
          </div>
        </header>

        {/* Inhalt */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4 bg-white">
          <StammdatenSection product={product} form={p} set={set} errorFields={ed.errorFields} />
          <PreiseSection form={p} set={set} errorFields={ed.errorFields} kundenpreise={ed.kundenpreise}
            kundenpreiseEditierbar={isAdmin && !isNew} kundenpreisEdits={ed.kundenpreisEdits}
            setKundenpreis={ed.setKundenpreis} kundenpreisErrorIds={ed.kundenpreisErrorIds} />
          <ProductEditorVarianten
            form={form} product={product} setRow={ed.setRow} addRow={ed.addRow} removeRow={ed.removeRow}
            errorFields={ed.errorFields} loading={ed.variantsLoading} />

          <Section icon={Recycle} title="EWK & Gesetzliches"
            hint="Einwegkunststofffondsgesetz (EWKFondsG): Produktart(en) wählen; die Abgabe ergibt sich aus Gewicht × Kunststoffanteil × Satz (€/kg).">
            <EwkKategorienPicker
              kategorien={ed.ewkKategorien}
              selected={p.ewk_kategorien || []}
              onChange={(list) => set('ewk_kategorien', list)}
              error={ed.ewkError}
            />
            {(p.ewk_kategorien || []).length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-3 items-start">
                <Field label="Kunststoffanteil %" htmlFor="pe-anteil" hint="Anteil des Gewichts, der Kunststoff ist.">
                  <NumInput id="pe-anteil" value={p.ewk_kunststoffanteil_prozent} invalid={ed.errorFields.has('ewk_kunststoffanteil_prozent')}
                    onChange={(e) => set('ewk_kunststoffanteil_prozent', e.target.value)} placeholder="100" />
                </Field>
                <EwkAbgabeVorschau rows={activeRows} schluessel={p.ewk_kategorien} anteilProzent={p.ewk_kunststoffanteil_prozent} kategorien={ed.ewkKategorien} />
              </div>
            )}
          </Section>

          <LagerSection isNew={isNew} product={product} form={p} set={set} errorFields={ed.errorFields} />
          <ProductEditorLieferanten suppliers={p.suppliers || []} onChange={(list) => set('suppliers', list)} supplierList={ed.supplierList} />
          {!isNew && <ProductEditorArchiv product={product} />}
          {!isNew && (
            <ProductEditorKanaele
              product={product}
              variants={ed.variants}
              dirty={ed.dirty}
              onPublished={() => { ed.invalidateAll(product.id); onOpenChange(false); }}
            />
          )}
        </div>

        {/* Fuß */}
        <footer className="shrink-0 border-t border-slate-200 bg-slate-50 px-4 sm:px-6 py-3 flex gap-2 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))]">
          <Button type="button" variant="outline" onClick={ed.requestClose} disabled={ed.saving} className="min-h-[44px]">
            <X className="w-4 h-4 mr-1.5" /> Abbrechen
          </Button>
          <Button type="button" onClick={ed.save} disabled={ed.saving || (!ed.dirty && !isNew)}
            className="flex-1 min-h-[44px] bg-brand-950 hover:bg-brand-900 text-white font-semibold">
            {ed.saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {ed.saving ? 'Speichern…' : isNew ? 'Produkt anlegen' : 'Speichern'}
          </Button>
        </footer>
      </SheetContent>
    </Sheet>
  );
}
